const initialiseKfaProductSequence = () => {
  const navigation = document.querySelector('[data-kfa-product-sequence]');
  if (!navigation) return;

  const CONTEXT_KEY = 'kfaProductSequenceContextV1';
  const params = new URLSearchParams(window.location.search);
  const currentHandle = navigation.dataset.productHandle;
  let collectionHandle = params.get('c_handle');
  let portalHandle = params.get('p_handle');

  const readStoredContext = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(CONTEXT_KEY) || '{}');
      if (stored.productHandle !== currentHandle || !stored.collectionHandle) return null;
      return stored;
    } catch (error) {
      return null;
    }
  };

  const storeContext = () => {
    if (!collectionHandle || !currentHandle) return;
    try {
      window.sessionStorage.setItem(CONTEXT_KEY, JSON.stringify({
        productHandle: currentHandle,
        collectionHandle,
        portalHandle: portalHandle || ''
      }));
    } catch (error) {
      // Navigation remains usable without session storage.
    }
  };

  if (!collectionHandle && currentHandle) {
    const stored = readStoredContext();
    if (stored) {
      collectionHandle = stored.collectionHandle;
      portalHandle = stored.portalHandle || '';
    }
  }

  storeContext();

  if (!collectionHandle || !currentHandle) return;

  const previousLink = navigation.querySelector('[data-kfa-product-prev]');
  const nextLink = navigation.querySelector('[data-kfa-product-next]');
  if (!previousLink || !nextLink) return;
  if (navigation.dataset.kfaSequenceInitialising === 'true') return;
  if (
    navigation.dataset.kfaSequenceReady === 'true' &&
    previousLink.getAttribute('href') !== '#' &&
    nextLink.getAttribute('href') !== '#'
  ) return;
  navigation.dataset.kfaSequenceInitialising = 'true';
  const shopRoot = window.Shopify?.routes?.root || '/';
  const normalizedRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
  const prefetchedPages = new Map();
  const imagePreloads = new Map();
  let navigationInProgress = false;
  let resizeFrame = null;

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const buildProductUrl = (handle) => {
    const url = new URL(`${normalizedRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('c_handle', collectionHandle);

    if (portalHandle) {
      url.searchParams.set('p_handle', portalHandle);
    }

    return `${url.pathname}${url.search}`;
  };

  const normalizeProducts = (products) => {
    const seen = new Set();

    return products
      .map((product) => ({
        handle: product?.handle || '',
        title: product?.title || product?.handle || ''
      }))
      .filter((product) => {
        if (!product.handle || seen.has(product.handle)) return false;
        seen.add(product.handle);
        return true;
      });
  };

  const loadFromProductsJson = async () => {
    const endpoint = `${normalizedRoot}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`;
    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Collection products request failed with ${response.status}`);
    }

    const payload = await response.json();
    return normalizeProducts(payload.products || []);
  };

  const loadFromCollectionHtml = async () => {
    const endpoint = `${normalizedRoot}collections/${encodeURIComponent(collectionHandle)}`;
    const response = await fetch(endpoint, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Collection page request failed with ${response.status}`);
    }

    const html = await response.text();
    const documentCopy = new DOMParser().parseFromString(html, 'text/html');
    const searchRoot = documentCopy.querySelector('main') || documentCopy;
    const products = [];

    searchRoot.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
      try {
        const url = new URL(anchor.getAttribute('href'), window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/);
        if (!match) return;

        const linkedCollection = url.searchParams.get('c_handle');
        const isCollectionPath = url.pathname.includes(`/collections/${collectionHandle}/products/`);

        if (linkedCollection && linkedCollection !== collectionHandle) return;
        if (!linkedCollection && !isCollectionPath) return;

        products.push({
          handle: decodeURIComponent(match[1]),
          title: anchor.getAttribute('aria-label') || anchor.textContent.trim()
        });
      } catch (error) {
        // Ignore malformed links and continue with the remaining collection links.
      }
    });

    return normalizeProducts(products);
  };

  const alignNavigationToProductColumn = () => {
    if (window.matchMedia('(max-width: 1280px)').matches) return;

    const productHeader = document.querySelector('.product-information__header');
    const positioningParent = navigation.offsetParent;
    if (!productHeader || !positioningParent) return;

    const targetRect = productHeader.getBoundingClientRect();
    const parentRect = positioningParent.getBoundingClientRect();
    const left = Math.max(0, targetRect.left - parentRect.left);
    const rightAllowance = Math.max(180, parentRect.right - targetRect.left - 150);

    navigation.style.setProperty('--kfa-product-sequence-left', `${left}px`);
    navigation.style.setProperty('--kfa-product-sequence-max-width', `${rightAllowance}px`);
  };

  const extractHeroImage = (html) => {
    const documentCopy = new DOMParser().parseFromString(html, 'text/html');
    const image = documentCopy.querySelector(
      '#MainContent .product-information slideshow-slide[aria-hidden="false"] .product-media__image, ' +
        '#MainContent .product-information slideshow-slide:first-of-type .product-media__image, ' +
        '#MainContent .product-information .product-media__image'
    );

    if (!image) return null;

    return {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || ''
    };
  };

  const preloadImage = (imageData) => {
    if (!imageData?.src && !imageData?.srcset) return Promise.resolve();

    const cacheKey = `${imageData.src}|${imageData.srcset}|${imageData.sizes}`;
    if (imagePreloads.has(cacheKey)) return imagePreloads.get(cacheKey);

    const preloadPromise = new Promise((resolve) => {
      const image = new Image();
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      image.decoding = 'async';
      image.fetchPriority = 'high';
      image.addEventListener('load', async () => {
        try {
          if (typeof image.decode === 'function') await image.decode();
        } catch (error) {
          // A decoded frame is preferable, but a completed network load is sufficient.
        }
        finish();
      });
      image.addEventListener('error', finish);

      if (imageData.sizes) image.sizes = imageData.sizes;
      if (imageData.srcset) image.srcset = imageData.srcset;
      if (imageData.src) image.src = imageData.src;

      if (image.complete && image.naturalWidth > 0) finish();
      window.setTimeout(finish, 4500);
    });

    imagePreloads.set(cacheKey, preloadPromise);
    return preloadPromise;
  };

  const warmProductPage = (url) => {
    const absoluteUrl = new URL(url, window.location.origin).href;
    if (prefetchedPages.has(absoluteUrl)) return prefetchedPages.get(absoluteUrl);

    // Safari/WebKit may route scripted HTML prefetches through Shopify's web
    // pixel observer and reject them as cross-site tracking. Navigation itself
    // remains reliable, so skip this optional optimisation on WebKit.
    const userAgent = navigator.userAgent || '';
    const isWebKit = /AppleWebKit/i.test(userAgent) && !/(Chrome|Chromium|CriOS|Edg)/i.test(userAgent);
    if (isWebKit) {
      const skipped = Promise.resolve('');
      prefetchedPages.set(absoluteUrl, skipped);
      return skipped;
    }

    const request = fetch(absoluteUrl, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      cache: 'default'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product prefetch failed with ${response.status}`);
        return response.text();
      })
      .then(async (html) => {
        await preloadImage(extractHeroImage(html));
        return html;
      })
      .catch((error) => {
        console.warn('KFA Produktvorschau konnte nicht vollständig vorgeladen werden.', error);
      });

    prefetchedPages.set(absoluteUrl, request);
    return request;
  };

  const markTransitionTrigger = (link) => {
    document.querySelectorAll('[data-view-transition-triggered]').forEach((element) => {
      element.removeAttribute('data-view-transition-triggered');
    });
    link.setAttribute('data-view-transition-triggered', 'true');
    link.setAttribute('data-view-transition-type', 'kfa-product-sequence');
  };

  const navigateWhenReady = async (event) => {
    const link = event.currentTarget;
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
    if (isModifiedClick || navigationInProgress || !link.href) return;

    event.preventDefault();
    navigationInProgress = true;
    navigation.setAttribute('aria-busy', 'true');
    link.classList.add('is-loading');

    markTransitionTrigger(link);

    // Keep the current work visible while the next page and its hero image are prepared.
    // A safety limit prevents a slow or unavailable asset from blocking navigation indefinitely.
    await Promise.race([warmProductPage(link.href), delay(5000)]);

    document.body.classList.add('kfa-product-transition--leaving');
    await delay(240);
    window.location.assign(link.href);
  };

  const bindLink = (link) => {
    ['pointerenter', 'focus', 'touchstart'].forEach((eventName) => {
      link.addEventListener(eventName, () => warmProductPage(link.href), { passive: true, once: eventName === 'touchstart' });
    });
    link.addEventListener('click', navigateWhenReady);
  };

  const initialise = async () => {
    let products = [];

    try {
      products = await loadFromProductsJson();
    } catch (jsonError) {
      try {
        products = await loadFromCollectionHtml();
      } catch (htmlError) {
        console.warn('KFA Werk-Navigation konnte nicht geladen werden.', jsonError, htmlError);
        return;
      }
    }

    if (products.length < 2) return false;

    const currentIndex = products.findIndex((product) => product.handle === currentHandle);
    if (currentIndex === -1) return false;

    const previousProduct = products[(currentIndex - 1 + products.length) % products.length];
    const nextProduct = products[(currentIndex + 1) % products.length];

    previousLink.href = buildProductUrl(previousProduct.handle);
    nextLink.href = buildProductUrl(nextProduct.handle);
    previousLink.setAttribute('aria-label', `Previous work: ${previousProduct.title}`);
    nextLink.setAttribute('aria-label', `Next work: ${nextProduct.title}`);
    previousLink.title = previousProduct.title;
    nextLink.title = nextProduct.title;

    bindLink(previousLink);
    bindLink(nextLink);
    alignNavigationToProductColumn();
    navigation.hidden = false;

    window.addEventListener('resize', () => {
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(alignNavigationToProductColumn);
    });

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const constrainedConnection = connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');

    if (window.matchMedia('(min-width: 1281px)').matches && !constrainedConnection) {
      const idleCallback = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 700));
      idleCallback(() => {
        warmProductPage(previousLink.href);
        warmProductPage(nextLink.href);
      });
    }

    return true;
  };

  initialise()
    .then((ready) => {
      if (ready) navigation.dataset.kfaSequenceReady = 'true';
    })
    .finally(() => {
      delete navigation.dataset.kfaSequenceInitialising;
    });
};

initialiseKfaProductSequence();

let kfaProductSequenceRefreshTimer = null;
const scheduleKfaProductSequenceRefresh = () => {
  if (kfaProductSequenceRefreshTimer) window.clearTimeout(kfaProductSequenceRefreshTimer);
  kfaProductSequenceRefreshTimer = window.setTimeout(initialiseKfaProductSequence, 80);
};

new MutationObserver(scheduleKfaProductSequenceRefresh).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['href']
});

document.addEventListener('shopify:section:load', scheduleKfaProductSequenceRefresh);
